import { motion, useReducedMotion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';

export const products = [
  {
    name: 'AudioXL MOBILE GROOVE',
    category: 'application',
    role: 'MOBILE BEATMAKING FOR IPHONE & IPAD',
    image: '/software/mobile-groove-two-iphones.png',
    position: 'center',
    fit: 'contain',
    portrait: true,
    composition: true,
    status: 'ONLINE',
    storeUrl: 'https://apps.apple.com/it/app/audioxl-mobile-groove/id6814764739',
    presentation: '/manuals/AudioXL-Mobile-Groove-Presentation.pdf',
    description:
      'Developed by AudioXL for iPhone and iPad, Mobile Groove brings eight synthesized drum voices, a 16/32-step sequencer, live sampling pads and MIDI or WAV export to your mobile studio. Available now on the App Store.',
    features: ['8 SYNTH VOICES', 'LIVE SAMPLING', 'MIDI & WAV EXPORT'],
    formats: 'iPhone & iPad',
  },
  {
    name: 'AudioXL ROUTING',
    category: 'application',
    role: 'SYSTEM-WIDE AUDIO ROUTING & MONITORING',
    image: '/software/routing.png',
    position: 'center',
    fit: 'contain',
    description:
      'Route every sound from your Mac through one shared Audio Unit processing chain, with transparent passthrough, real-time metering and MIDI control.',
    features: ['SYSTEM-WIDE CAPTURE', 'AU PLUGIN CHAIN', 'MIDI CONTROL'],
    formats: 'macOS 14.2+ · WIN · Standalone',
    status: 'RELEASE SOON',
  },
  {
    name: 'AudioXL SEQUENCER',
    role: 'GENERATIVE MIDI SEQUENCER',
    image: '/software/sequencer-full-20260922.jpg',
    position: 'center',
    fit: 'contain',
    description:
      'Seventeen independent lanes turn rhythm, pitch, harmony and modulation into patterns that keep evolving while staying in key.',
    features: ['17 LANES', '16 MIDI CHANNELS', '312 PRESETS'],
    status: 'RELEASE SOON',
  },
  {
    name: 'AudioXL DRUMS',
    role: 'SYNTH & SAMPLE DRUM MACHINE',
    image: '/software/drums-20260922.jpg',
    position: 'center',
    fit: 'contain',
    description:
      'Eight synthesized and sampled voices, a sixteen-step sequencer and a complete effects rack in one focused instrument.',
    features: ['8 VOICES', '3,469 SAMPLES', '136 GROOVES'],
    status: 'RELEASE SOON',
  },
  {
    name: 'AudioXL MOTION FX',
    role: 'CREATIVE MOTION SHAPER',
    image: '/software/motion-fx-20260922.jpg',
    position: 'center',
    fit: 'contain',
    description:
      'Sequence filters, volume and pan, then reshape audio with beat repeat, drive, modulation and granular movement.',
    features: ['4 SEQUENCED LANES', '27 TARGETS', '145 PRESETS'],
    status: 'RELEASE SOON',
  },
  {
    name: 'AudioXL MASTER RACK',
    role: 'BUS COLOUR & DYNAMICS',
    image: '/software/master-rack-20260922.jpg',
    fit: 'contain',
    position: 'center',
    description:
      'The character of the AudioXL DRUMS master chain, expanded into a standalone processor for tracks, buses and full mixes.',
    features: ['11 PROCESSORS', '46 PRESETS', 'DRUMS SYNC'],
    status: 'RELEASE SOON',
  },

];

const Software = () => {
  const [category, setCategory] = useState('application');
  const visibleProducts = products.filter(product => {
    if (product.hidden) return false;
    return (product.category || 'plugin') === category;
  });
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const reduceMotion = useReducedMotion();

  return (
    <section
      id="software"
      data-testid="software-section"
      ref={ref}
      className="relative scroll-mt-24 overflow-hidden bg-[#080808] px-6 py-32 md:scroll-mt-28 md:px-12 md:py-48 lg:px-24"
    >
      <div className="relative z-10 mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20 md:mb-32"
        >
          <span className="font-heading text-sm font-bold uppercase tracking-[0.3em] text-white/40">
            Built In Milan
          </span>
          <h2
            className="font-heading mt-6 text-3xl font-bold leading-tight md:text-5xl lg:text-6xl"
            data-testid="software-heading"
          >
            SOFTWARE
          </h2>
          <p className="font-body mt-8 max-w-2xl text-lg leading-relaxed text-white/55 md:text-xl">
            Instruments and effects designed inside the studio, shaped by the way we make electronic music every day.
          </p>
        </motion.div>

        <nav className="software-category-menu font-heading" aria-label="Software categories">
          {[['application', 'Application'], ['plugin', 'Plugin'], ['free', 'Free software']].map(([id, label]) => (
            <button key={id} type="button" aria-pressed={category === id} aria-controls="software-category-content" onClick={() => setCategory(id)}>
              {label}<span aria-hidden="true">↗</span>
            </button>
          ))}
        </nav>
        <div id="software-category-content" aria-live="polite">
        {category !== 'free' && <div className="border-t border-white/10">
          {visibleProducts.map((product, index) => (
            <motion.article
              key={product.name}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.8,
                delay: 0.15 + index * 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
              className={`software-product group grid grid-cols-1 gap-8 border-b border-white/10 py-10 md:py-14 lg:grid-cols-12 lg:gap-12 ${product.portrait ? 'lg:items-stretch' : 'lg:items-center'}`}
              data-testid={`software-product-${index}`}
            >
              <div className="lg:col-span-4">
                <span className="font-body text-xs uppercase tracking-[0.24em] text-white/60">
                  {product.role}
                </span>
                <h3 className="font-heading mt-4 text-2xl font-bold leading-tight text-white md:text-3xl">
                  {product.name}
                </h3>
                <p className="font-body mt-5 max-w-md text-base leading-relaxed text-white/55 transition-colors duration-500 group-hover:text-white/75">
                  {product.description}
                </p>
                <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
                  {product.features.map((feature) => (
                    <span
                      key={feature}
                      className="font-body text-xs font-medium uppercase tracking-[0.18em] text-white/55"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
                <div className="inline-flex items-center gap-3 border border-white/15 px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-white/60">
                  {product.formats || 'macOS · WIN · VST3 · AU'}
                </div>
                {product.status && (
                  <div className={`software-status font-heading inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.18em] ${product.storeUrl ? 'software-status-online' : 'text-[#ff7a73]'}`}>
                    <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${product.storeUrl ? 'software-status-online-dot' : 'bg-[#ff5a52] shadow-[0_0_12px_rgba(255,90,82,0.55)]'}`} />
                    {product.status}
                  </div>
                )}
                </div>
                {(product.storeUrl || product.presentation) && (
                  <div className="mt-7 flex flex-wrap gap-3">
                    {product.storeUrl && (
                      <a
                        href={product.storeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="software-free-cta font-heading inline-flex w-fit items-center gap-3 px-5 py-4 text-xs font-bold uppercase tracking-[0.16em]"
                        data-testid="mobile-groove-app-store"
                      >
                        Available on the App Store <span aria-hidden="true">↗</span>
                      </a>
                    )}
                    {product.presentation && (
                      <a
                        href={product.presentation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-heading inline-flex w-fit items-center gap-3 border border-white/15 px-5 py-4 text-xs font-bold uppercase tracking-[0.16em] text-white/60 transition-colors duration-300 hover:border-white/35 hover:text-white"
                        data-testid="mobile-groove-presentation"
                      >
                        Presentation · PDF <span aria-hidden="true">↗</span>
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="lg:col-span-8">
                <div
                  className={`software-screen relative overflow-hidden border border-white/10 bg-[#020305] ${product.portrait ? 'software-mobile-screen min-h-[620px] md:min-h-[760px]' : 'aspect-[16/9]'}`}
                >
                  {product.portrait ? (
                    <div className={product.composition ? 'absolute inset-4 flex items-center justify-center md:inset-8' : 'software-mobile-device absolute inset-y-4 left-1/2 aspect-[842/1714] -translate-x-1/2 overflow-hidden md:inset-y-8'}>
                      <motion.img
                        src={product.image}
                        alt={product.composition ? `${product.name} shown on two iPhones` : `${product.name} interface`}
                        loading="lazy"
                        className={`h-full w-full ${product.composition ? 'object-contain' : 'object-cover'} opacity-90 transition-opacity duration-700 group-hover:opacity-100`}
                        whileHover={reduceMotion ? undefined : { scale: 1.012 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  ) : (
                    <motion.img
                      src={product.image}
                      alt={`${product.name} interface`}
                      loading="lazy"
                      className={`h-full w-full ${product.fit === 'contain' ? 'object-contain' : 'object-cover'} opacity-75 grayscale-[20%] transition-all duration-700 group-hover:opacity-100 group-hover:grayscale-0`}
                      style={{ objectPosition: product.position }}
                      whileHover={reduceMotion ? undefined : { scale: 1.025 }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/25 via-transparent to-transparent" />
                  <div className="software-scanline pointer-events-none absolute inset-x-0 top-0 h-px bg-white/50" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>}

        {category === 'free' && <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: reduceMotion ? 0 : 0.4 }}
          className="border-t border-white/10 pt-10 md:pt-14"
          data-testid="free-software-section"
        >
          <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <span className="font-heading text-xs font-bold uppercase tracking-[0.3em] text-[#24d9e8]">
                Free Tools
              </span>
              <h3 className="font-heading mt-4 text-3xl font-bold leading-none text-white md:text-5xl">
                FREE SOFTWARE
              </h3>
            </div>
            <p className="font-body max-w-lg text-sm leading-relaxed text-white/60 md:text-right">
              Focused music tools, made in the AudioXL studio and available to download for free.
            </p>
          </div>

          <article className="software-free group relative overflow-hidden border border-white/10 bg-[#0d1114]">
            <div className="h-1 w-full bg-[#24d9e8]" />
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="flex flex-col justify-center border-b border-white/10 p-7 md:p-10 lg:col-span-4 lg:border-b-0 lg:border-r">
                <div className="flex items-center gap-3">
                  <span className="font-body border border-[#24d9e8]/50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#24d9e8]">
                    Free Download
                  </span>
                  <span className="font-body text-xs uppercase tracking-[0.2em] text-white/60">
                    Max for Live
                  </span>
                </div>
                <h4 className="font-heading mt-6 text-2xl font-bold leading-tight text-white md:text-3xl">
                  AudioXL ARPEGGIATOR
                </h4>
                <p className="font-body mt-5 text-base leading-relaxed text-white/55 transition-colors duration-500 group-hover:text-white/75">
                  A MIDI arpeggiator inspired by the Access Virus B and first Virus Indigo, with the complete bank of 64 original patterns and a hands-on sixteen-step editor.
                </p>
                <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
                  {['64 ORIGINAL PATTERNS', '16-STEP EDITOR', 'RANDOMIZE'].map((feature) => (
                    <span
                      key={feature}
                      className="font-body text-xs font-medium uppercase tracking-[0.18em] text-white/55"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <a
                  href="https://maxforlive.com/library/device/15887/audioxl-arpeggiator"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="software-free-cta font-heading mt-9 inline-flex w-fit items-center gap-3 px-5 py-4 text-xs font-bold uppercase tracking-[0.16em]"
                  data-testid="arpeggiator-download"
                >
                  Download free <span aria-hidden="true">↗</span>
                </a>
              </div>

              <a
                href="https://maxforlive.com/library/device/15887/audioxl-arpeggiator"
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex min-h-[250px] items-center bg-[#030506] p-4 md:p-8 lg:col-span-8"
                aria-label="Download AudioXL Arpeggiator from Max for Live"
              >
                <img
                  src="/software/arpeggiator.png"
                  alt="AudioXL Arpeggiator interface"
                  loading="lazy"
                  className="h-auto w-full opacity-80 transition-all duration-700 group-hover:scale-[1.01] group-hover:opacity-100"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#24d9e8]/[0.04]" />
              </a>
            </div>
          </article>

          <article className="software-free group relative mt-10 overflow-hidden border border-white/10 bg-[#0d1114] md:mt-14 lg:mt-16">
            <div className="h-1 w-full bg-[#24d9e8]" />
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="flex flex-col justify-center border-b border-white/10 p-7 md:p-10 lg:col-span-4 lg:border-b-0 lg:border-r">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-body border border-[#24d9e8]/50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#24d9e8]">
                    Free Download
                  </span>
                  <span className="font-body text-xs uppercase tracking-[0.2em] text-white/60">
                    VST3 · AU · Standalone
                  </span>
                </div>
                <h4 className="font-heading mt-6 text-2xl font-bold leading-tight text-white md:text-3xl">
                  AudioXL VINTAGE PITCHER
                </h4>
                <p className="font-body mt-5 text-base leading-relaxed text-white/55 transition-colors duration-500 group-hover:text-white/75">
                  Detect the key of loops or live tracks and shift them into the key of your song, with real-time plugin use and a standalone file workflow.
                </p>
                <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
                  {['KEY DETECTION', 'LIVE MODE', 'FILE EXPORT'].map((feature) => (
                    <span
                      key={feature}
                      className="font-body text-xs font-medium uppercase tracking-[0.18em] text-white/55"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="mt-9 flex flex-wrap gap-3">
                  <a
                    href="/downloads/AudioXL-Vintage-Pitcher-1.0.0.dmg"
                    className="software-free-cta font-heading inline-flex w-fit items-center gap-3 px-5 py-4 text-xs font-bold uppercase tracking-[0.16em]"
                    data-testid="vintage-pitcher-download"
                  >
                    Download free <span aria-hidden="true">↗</span>
                  </a>
                  <a
                    href="/manuals/AudioXL-Vintage-Pitcher-Manual-EN.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-heading inline-flex w-fit items-center gap-3 border border-white/15 px-5 py-4 text-xs font-bold uppercase tracking-[0.16em] text-white/60 transition-colors duration-300 hover:border-white/35 hover:text-white"
                  >
                    Manual <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </div>

              <a
                href="/downloads/AudioXL-Vintage-Pitcher-1.0.0.dmg"
                className="relative flex min-h-[250px] items-center bg-[#030506] p-4 md:p-8 lg:col-span-8"
                aria-label="Download AudioXL Vintage Pitcher"
              >
                <img
                  src="/software/vintage-pitcher.png"
                  alt="AudioXL Vintage Pitcher interface"
                  loading="lazy"
                  className="h-auto w-full opacity-80 transition-all duration-700 group-hover:scale-[1.01] group-hover:opacity-100"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#24d9e8]/[0.04]" />
              </a>
            </div>
          </article>
        </motion.div>}
        </div>

        {category !== 'free' && <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center"
        >
          <p className="font-body max-w-xl text-sm leading-relaxed text-white/60">
            AudioXL Mobile Groove is available now on the App Store. More AudioXL releases are being prepared for public launch.
          </p>
          <a
            href="#contact"
            className="software-cta font-heading text-xs font-bold uppercase tracking-[0.16em] text-white"
          >
            Ask for early access <span aria-hidden="true">↗</span>
          </a>
        </motion.div>}
      </div>
    </section>
  );
};

export default Software;
